import { App, watch } from 'vue';
import type { Router } from 'vue-router';
import { useTenantStore } from './stores/useTenantStore';
import { storeToRefs } from 'pinia';

export async function initSentry(app: App, router: Router) {
  const VITE_ENV = import.meta.env.VITE_ENV?.toUpperCase() || 'DEVELOPMENT';
  if (VITE_ENV === 'DEVELOPMENT') {
    return;
  } else {
    console.info('Patient Portal version:', import.meta.env.RELEASE_NUMBER || 'unknown');
    if (VITE_ENV !== 'PRODUCTION' && VITE_ENV !== 'PROD') console.info('Sentry init', VITE_ENV);
  }

  if (!import.meta.env.VITE_PATIENT_PORTAL_SENTRY_DSN) {
    if (VITE_ENV !== 'PRODUCTION' && VITE_ENV !== 'PROD') console.info('Sentry no DSN');
    return;
  }

  const tenantStore = useTenantStore();
  const { tenant } = storeToRefs(tenantStore);
  try {
    const Sentry = await import('@sentry/vue');

    Sentry.init({
      app,
      dsn: import.meta.env.VITE_PATIENT_PORTAL_SENTRY_DSN,
      release: import.meta.env.RELEASE_NUMBER,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          // Block sensitive data from being sent to Sentry
          mask: ['[data-private]', '.phi', '.pii'],
          block: ['[data-block]', '.replay-block'],
        }),
        Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      ],
      environment: VITE_ENV,
      tracesSampleRate: 0.05,
      replaysSessionSampleRate: 0.03,
      replaysOnErrorSampleRate: 0.5,
      //
      enableLogs: true,
      // ENG-20636 Belt-and-braces: drop info-level captures (they belong in
      // analytics, not Sentry) plus any known-noise messages that might slip
      // back in. The explicit captureMessage sites that generated these have
      // been removed in the same change, but the filter stops new ones from
      // re-introducing the same waste.
      beforeSend(event) {
        if (event.level === 'info') return null;

        // ENG-20636 Only filter messages that were structured-logging via
        // Sentry. Known error-code captures from Error.vue:121 and the
        // suppressed-API-error capture from useApiClientStore.ts are kept —
        // they fire at level:'error' so they pass through this filter.
        const msg = event.message ?? '';
        const noise = [/ submitted successfully$/i, /^session timeout$/i];
        if (noise.some((p) => p.test(msg))) return null;
        return event;
      },
    });

    watch(
      () => tenant.value?.tenantId,
      (tenantId) => {
        Sentry.setTag('tenant', tenantId || 'unknown');
      },
      { immediate: true },
    );
  } catch (err) {
    console.warn('Sentry init failed', err);
  }
}
